/**
 * @file `CoreAnalytics` — the single home for LabCraft's analytics logic:
 *       acquisition classification, cohort-week bucketing, and the
 *       identify-payload PII shaping shared by every SPA.
 * @module @labcraft/core/shared/core-analytics
 *
 * One class consolidates what were three scattered modules
 * (`acquisition.ts`, `cohort.ts`, and the non-DOM half of
 * `@labcraft/ui`'s `use_analytics.ts`). Everything here is a **pure
 * transform or a bounded schema** — no `this`, no observability, no DOM,
 * no `node:` — so the methods are all `static` per
 * `.agents/rules/typescript.md` § "Functions belong on classes". It still
 * `extends CoreClass` so the analytics vocabulary has one discovery
 * surface and so any future stateful analytics helper (an instance method
 * that emits telemetry) has a home already carrying identity + logging.
 *
 * Lives in `@labcraft/core/shared` (the zod-only, node-free, DOM-free
 * layer) so both sides of every wire share one source of truth: the
 * browser-side capture leaves (`@labcraft/core/frontend/acquisition.ts`),
 * the lab/account wire schemas, the account daemon's DB-boundary
 * re-validation, and the lab SPA's identify caller all import from here.
 *
 * ## Acquisition — a compound type (granular source + coarse category)
 *
 * A visitor's acquisition is one {@link Acquisition} value — a plain
 * compound `{ source, category, campaign }` pairing a granular `source`
 * with a derived coarse `category` and the specific campaign name —
 * captured at first sign-up (umbrella #1213, Phase 1 / #1214):
 *
 *   - `source` — the granular channel: an OPEN sanitized string (the
 *     `utm_source` token if present, else the referrer HOST, else `null`).
 *     Preserves the long tail — `discourse.nixos.org` stays
 *     `discourse.nixos.org` instead of collapsing to a coarse bucket.
 *   - `category` — a DERIVED coarse enum (`campaign` | `organic` |
 *     `referral` | `direct`) for funnels that want the four-way split.
 *     Always present on an `Acquisition` — a captured attribution always
 *     has a category (`source` alone is nullable).
 *   - `campaign` — the specific campaign name: the sanitized `utm_campaign`
 *     token, or `null` when the visit carried no `utm_campaign` tag (every
 *     non-`campaign` category, and a `campaign` visit tagged only with
 *     `utm_source` / `utm_medium`). Populated when available so a signup
 *     can be linked to the initiative that drove it, not just the channel.
 *     Shares the `source` charset/length bound (a sanitized token).
 *
 * **PII invariant (umbrella-wide):** the stored `source` is always a
 * sanitized string (lowercase, `[a-z0-9._-]` only, ≤64 chars) — never a
 * raw `document.referrer` or raw `utm_*` value. The referrer contributes
 * only its **host** (path + query, which can carry PII, are dropped in
 * {@link CoreAnalytics.hostOf}); a `utm_source` value is sanitized so the
 * abuse surface is bounded. `{@link CoreAnalytics.classifyAcquisition}`
 * owns the collapse so the raw strings never leave the browser; the
 * account daemon re-validates the arriving `source` via
 * {@link CoreAnalytics.sanitizeSource} and the `category` via
 * {@link CoreAnalytics.AcquisitionCategorySchema} at the DB boundary.
 *
 * **Three states, expressed at the Learner level.** "Never captured" is
 * NOT a null `category` — it is a null {@link Acquisition} altogether. A
 * `learners` row carries `acquisition: Acquisition | null` (a plain
 * `{ source, category } | null`):
 *
 *   - `null` — never captured (row predates capture, or the browser
 *     supplied nothing that could be classified).
 *   - `{ source: null, category: 'direct', campaign: null }` — captured,
 *     but no referrer/utm (typed the URL, a bookmark, a referrer-stripping
 *     app).
 *   - `{ source: 'discourse.nixos.org', category: 'referral', campaign:
 *     null }` — captured from a real channel with no campaign tag.
 *   - `{ source: 'twitter', category: 'campaign', campaign: 'nix-launch' }`
 *     — captured from a named campaign.
 *
 * So an {@link Acquisition} value is always a real captured attribution:
 * its `category` is never `null`; the "never captured" state lives one
 * level up as a null `Acquisition`. The operator wants that distinction
 * for cohort analysis, so the classifier always returns an `Acquisition`
 * (never `null`) whenever it runs — a clean landing yields
 * `{ source: null, category: 'direct' }`, not `null`.
 *
 * ## DB shape — three columns map to the one compound value
 *
 * `Acquisition` is a compound `{ source, category, campaign }` on the wire
 * + in memory, but `learners` stores it as **three nullable columns**
 * (`acquisition_source`, `acquisition_category`, `acquisition_campaign`).
 * The mapping: `acquisition_category IS NULL` ⟺ never captured ⟺
 * `acquisition = null`; otherwise `acquisition = { source:
 * acquisition_source, category: acquisition_category, campaign:
 * acquisition_campaign }`. This is the same
 * compound-maps-to-multiple-columns shape `Learner` already uses (one
 * object, many columns). The compound object is flattened back to scalars
 * at the structural edges that can't hold a nested object — the OAuth
 * `acq_source`/`acq_cat`/`acq_campaign` query params, the `auth_signup`
 * Umami event props, and the `umami.identify()` session props. `category`
 * is the presence signal (never null on a captured row); `source` and
 * `campaign` are each independently nullable.
 *
 * ## Cohort week — ISO-8601 `YYYY-Www` bucketing
 *
 * A learner's cohort — the ISO-8601 week they first signed up in — is
 * derived from `learners.created_at` and passed into `umami.identify()`
 * as a session property so retention can be sliced by acquisition cohort
 * (umbrella #1213, Phase 2 / #1217). The raw `created_at` timestamp never
 * reaches Umami; only the coarse `YYYY-Www` bucket does.
 *
 * ## Identify payload — the PII-typed session shaping
 *
 * The `SessionValue` / `SessionData` types are the non-DOM half of the
 * identify wrapper. The `@labcraft/ui` hook keeps the `window.umami` DOM
 * calls but delegates the scalar-only value typing here, so the PII floor
 * (scalar-only session-property values) lives in one place. The distinct
 * id is the caller's already-validated `learners.id` UUID
 * (`LearnerSchema.id` = `z.string().uuid()`); a domain-wide `LearnerId`
 * brand is tracked separately in #1241.
 */

import { z } from 'zod'
import { CoreClass } from './core_class.ts'

////////////////////////////////////////////////////////////////////////////////
// Identify payload types
////////////////////////////////////////////////////////////////////////////////

/**
 * The only value types an identify session property may carry: coarse
 * scalars, never a nested object or array. A session property is a bounded
 * enum member or a bucketed / sanitized string (a plan tier, a `YYYY-Www`
 * cohort, an acquisition category, a {@link CoreAnalytics.sanitizeSource}'d
 * source), so nothing richer than a scalar has any place here. The
 * type-level half of the PII invariant: a `{ email }`-shaped payload whose
 * value is an object simply doesn't type-check as a {@link SessionData}.
 */
export type SessionValue = string | number | boolean

/**
 * A session-identity payload for the identify wrapper: a closed record
 * whose values are all {@link SessionValue} scalars. The caller supplies a
 * fully-bounded closed record (each value a closed-enum member, a
 * regex-validated bucket, or a sanitized safe string), which is where the
 * "no email/name/raw-referrer key can exist" guarantee is pinned. This
 * shape stays dependency-agnostic so the `@labcraft/ui` wrapper can enforce
 * the scalar-value floor without importing `@labcraft/core`; the caller's
 * closed type enforces the bounded-key ceiling.
 */
export type SessionData = Record<string, SessionValue>

////////////////////////////////////////////////////////////////////////////////
// Acquisition types
////////////////////////////////////////////////////////////////////////////////

/**
 * The derived coarse acquisition category — the four-way split for
 * funnels that don't want the granular `source` long tail. Distinct from
 * `null` (which means "never captured") so `direct` (captured, nothing)
 * stays visible.
 */
export type AcquisitionCategory = z.infer<
  typeof CoreAnalytics.AcquisitionCategorySchema
>

/**
 * Inputs to {@link CoreAnalytics.classifyAcquisition}. Both are the raw
 * browser-side signals; the classifier is the only place they are read —
 * the sanitized `source` + derived `category` are all that leave.
 *
 * @property referrer - The raw `document.referrer` (an absolute URL, or an
 *   empty string when absent). Never persisted; only its host is inspected.
 * @property params - The landing URL's query parameters (typically
 *   `new URLSearchParams(window.location.search)`). Only `utm_*` presence
 *   and the `utm_source` value are inspected; the value is sanitized before
 *   it can leave.
 */
export type AcquisitionSignals = {
  referrer: string
  params: URLSearchParams
}

////////////////////////////////////////////////////////////////////////////////
// CoreAnalytics
////////////////////////////////////////////////////////////////////////////////

/**
 * The single home for LabCraft's analytics logic. Pure transforms +
 * bounded schemas as `static` members; extends `CoreClass` so the
 * analytics vocabulary sits on one class and a future stateful helper has
 * a home. Never instantiated today — every member is static.
 */
export class CoreAnalytics extends CoreClass {
  //////////////////////////////////////////////////////////////////////////////
  // Acquisition — schemas
  //////////////////////////////////////////////////////////////////////////////

  /**
   * The derived coarse acquisition category enum. Four buckets for funnel
   * segmentation:
   *   - `campaign` — any `utm_*` tag was present.
   *   - `organic` — the referrer host is a known search engine.
   *   - `referral` — any other non-empty referrer.
   *   - `direct` — no referrer and no utm (typed the URL, a bookmark, an
   *     app that strips the referrer). Distinct from a `null` column, which
   *     means "never captured".
   *
   * The single source of truth the classifier produces, the lab/account
   * wire schemas embed (`.nullable()`), and the account daemon re-validates
   * arriving values against at the DB boundary.
   */
  static readonly AcquisitionCategorySchema = z.enum([
    'campaign',
    'organic',
    'referral',
    'direct',
  ])

  /**
   * The granular acquisition-source schema: a sanitized open string
   * (lowercase, `[a-z0-9._-]` only, ≤64 chars). Not an enum — it preserves
   * the long tail (`discourse.nixos.org`, an arbitrary `utm_source` token).
   * The bounded charset + length is the PII/abuse ceiling; the field sites
   * apply `.nullable()` since a source can be absent. The classifier
   * produces values that satisfy this by construction (via
   * {@link sanitizeSource}), and the account daemon re-validates the
   * arriving value against {@link sanitizeSource} at the DB boundary.
   */
  static readonly AcquisitionSourceSchema = z
    .string()
    .min(1)
    .max(64)
    .regex(
      /^[a-z0-9._-]+$/,
      'acquisition source must be lowercase [a-z0-9._-], ≤64 chars',
    )

  /**
   * Hostname fragments that identify a search engine. A referrer whose
   * host contains one of these (and that carries no utm tag) classifies to
   * `organic`. Substring match keeps regional variants (`google.co.uk`,
   * `bing.com`) and subdomains (`www.google.com`) in one entry. A
   * classification heuristic, not a data-losing enum — the granular
   * `source` still records the exact host.
   */
  static readonly SEARCH_ENGINE_HOSTS = [
    'google.',
    'bing.',
    'duckduckgo.',
    'yahoo.',
    'ecosia.',
    'brave.',
    'startpage.',
    'yandex.',
    'baidu.',
  ] as const

  //////////////////////////////////////////////////////////////////////////////
  // Acquisition — classification
  //////////////////////////////////////////////////////////////////////////////

  /**
   * Collapse a visitor's raw referrer + utm params into a granular open
   * `source` + a derived coarse `category`. Pure and runtime-neutral (no
   * DOM, no `node:`), so it is unit-testable in isolation and safe to run
   * in the browser, a Service Worker, or Node.
   *
   * Precedence (first match wins):
   *   1. Any `utm_*` tag present → `category = 'campaign'`,
   *      `source = sanitizeSource(utm_source)` (or `null` when there is a
   *      `utm_medium` / `utm_campaign` but no `utm_source` value),
   *      `campaign = sanitizeSource(utm_campaign)` (or `null` when the tag
   *      set carries no `utm_campaign`).
   *   2. A referrer from a known search-engine host → `category =
   *      'organic'`, `source = sanitizeSource(host)`, `campaign = null`.
   *   3. Any other non-empty referrer → `category = 'referral'`,
   *      `source = sanitizeSource(host)`, `campaign = null`.
   *   4. No referrer and no utm tag → `category = 'direct'`, `source =
   *      null`, `campaign = null`.
   *
   * The raw strings never leave this function: only the sanitized `source`
   * (host-only + charset-bounded), the coarse `category`, and the sanitized
   * `campaign` token are exposed, upholding the umbrella's PII invariant at
   * the capture boundary.
   *
   * Always returns an {@link Acquisition} — a run of the classifier is a
   * captured attribution, so it never returns `null`. A clean landing
   * yields `{ source: null, category: 'direct' }`; the "never captured"
   * state is a null `Acquisition` one level up (on the `learners` row),
   * not a return value here.
   *
   * @param signals - The raw referrer + landing query params.
   * @returns The captured {@link Acquisition}.
   */
  static classifyAcquisition(signals: AcquisitionSignals): Acquisition {
    const hasUtmTag =
      signals.params.has('utm_source') ||
      signals.params.has('utm_medium') ||
      signals.params.has('utm_campaign')

    if (hasUtmTag) {
      return {
        source: CoreAnalytics.sanitizeSource(signals.params.get('utm_source')),
        category: 'campaign',
        campaign: CoreAnalytics.sanitizeSource(
          signals.params.get('utm_campaign'),
        ),
      }
    }

    const referrerHost = CoreAnalytics.hostOf(signals.referrer)
    if (referrerHost) {
      const isSearch = CoreAnalytics.SEARCH_ENGINE_HOSTS.some(h =>
        referrerHost.includes(h),
      )
      return {
        source: CoreAnalytics.sanitizeSource(referrerHost),
        category: isSearch ? 'organic' : 'referral',
        campaign: null,
      }
    }

    return { source: null, category: 'direct', campaign: null }
  }

  /**
   * Sanitize a raw source string into the bounded {@link
   * AcquisitionSourceSchema} charset: lowercase, trim, keep only
   * `[a-z0-9._-]`, cap length at 64. Returns `null` when the input is
   * absent, empty, or all-stripped. The classifier funnels every
   * candidate source (utm token, referrer host) through here so the stored
   * `source` is always PII- and abuse-bounded; the account daemon runs the
   * arriving + stored value through here again at the DB boundary as
   * defense-in-depth.
   *
   * @param raw - The candidate source (`utm_source` value or referrer
   *   host), or `null` / `undefined` when absent.
   * @returns The sanitized source, or `null`.
   */
  static sanitizeSource(raw: string | null | undefined): string | null {
    if (!raw) return null
    const cleaned = raw
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9._-]/g, '')
      .slice(0, 64)
    return cleaned.length > 0 ? cleaned : null
  }

  /**
   * Parse the host out of a referrer URL, lower-cased, or `null` when the
   * referrer is empty or unparseable. Only the host is ever read — the
   * path and query (which can carry PII) are discarded here and never
   * returned. Private: callers reach acquisition via
   * {@link classifyAcquisition}, which sanitizes the host before exposing
   * it.
   *
   * @param referrer - The raw `document.referrer` string.
   * @returns The lower-cased host, or `null`.
   */
  private static hostOf(referrer: string): string | null {
    if (!referrer) return null
    try {
      return new URL(referrer).host.toLowerCase() || null
    } catch {
      return null
    }
  }

  //////////////////////////////////////////////////////////////////////////////
  // Cohort week — ISO-8601 bucketing
  //////////////////////////////////////////////////////////////////////////////

  /**
   * Shape of a cohort-week bucket: an ISO-8601 week-date of the form
   * `YYYY-Www` (four-digit ISO week-year, literal `-W`, two-digit
   * zero-padded week number 01–53). The closed contract — an arbitrary
   * string (an email, a name, a raw timestamp) cannot satisfy it, so
   * validating against this pattern at the identify boundary upholds the
   * umbrella's PII invariant even for a value that was mis-derived.
   */
  static readonly COHORT_WEEK_PATTERN = /^\d{4}-W(0[1-9]|[1-4]\d|5[0-3])$/

  /**
   * Zod schema for a cohort-week bucket. The single source of truth the
   * lab SPA's identify caller produces (via {@link bucketCohortWeek}) and
   * the identify wrapper's session-property validation embeds. A branded
   * string type so a bare `string` can't be assigned where a validated
   * bucket is required — the brand is the type-level half of the same PII
   * guard the regex enforces at runtime.
   */
  static readonly CohortWeekSchema = z
    .string()
    .regex(
      CoreAnalytics.COHORT_WEEK_PATTERN,
      'cohort week must be ISO-8601 YYYY-Www',
    )
    .brand<'CohortWeek'>()

  /**
   * Number of milliseconds in one day — the unit the ISO-week arithmetic
   * counts in after both dates are normalised to UTC midnight.
   */
  private static readonly MS_PER_DAY = 24 * 60 * 60 * 1000

  /**
   * Collapse a signup `Date` into its ISO-8601 `YYYY-Www` cohort-week
   * bucket. Pure and runtime-neutral (no DOM, no `node:`), so it is
   * unit-testable in isolation and safe to run in the browser, a Service
   * Worker, or Node. The raw `Date` never leaves this function — only the
   * bounded, regex-shaped string is returned, upholding the umbrella's PII
   * invariant at the derivation boundary.
   *
   * Computed in UTC end-to-end so the bucket is deployment- and
   * viewer-timezone-independent (`created_at` is a UTC instant; the cohort
   * a learner belongs to must not depend on where the report is read).
   *
   * The algorithm is the canonical ISO week-date derivation:
   *   1. Shift the date to the Thursday of its ISO week (Thursday's year is
   *      the ISO week-year by definition).
   *   2. Week number = how many weeks that Thursday is past the year's
   *      first Thursday, plus one.
   *
   * @param createdAt - The learner's signup instant (`learners.created_at`).
   * @returns The `YYYY-Www` bucket, validated as a {@link CohortWeek}.
   */
  static bucketCohortWeek(createdAt: Date): CohortWeek {
    // Normalise to UTC midnight so intra-day time never perturbs the week.
    const date = new Date(
      Date.UTC(
        createdAt.getUTCFullYear(),
        createdAt.getUTCMonth(),
        createdAt.getUTCDate(),
      ),
    )

    // ISO weekday: Monday=1 … Sunday=7 (JS `getUTCDay()` is Sunday=0).
    const isoDay = date.getUTCDay() === 0 ? 7 : date.getUTCDay()

    // Step 1: move to the Thursday of this week. Thursday is ISO day 4, so
    // the shift is `4 - isoDay` days. Thursday's calendar year IS the ISO
    // week-year — this is what makes an early-January date resolve to the
    // previous year's week 52/53 (and vice versa) correctly.
    const thursday = new Date(date)
    thursday.setUTCDate(date.getUTCDate() + 4 - isoDay)

    const isoYear = thursday.getUTCFullYear()

    // Step 2: week number = weeks between this Thursday and Jan 1 of the
    // ISO year, +1. Jan 1's own week is week 1 whenever Jan 1 is a Thursday.
    const yearStart = new Date(Date.UTC(isoYear, 0, 1))
    const week =
      Math.floor(
        (thursday.getTime() - yearStart.getTime()) /
          CoreAnalytics.MS_PER_DAY /
          7,
      ) + 1

    const bucket = `${isoYear}-W${String(week).padStart(2, '0')}`

    // Re-validate through the schema so the return is a branded CohortWeek
    // and any arithmetic slip fails loud here rather than reaching Umami as
    // a malformed bucket.
    return CoreAnalytics.CohortWeekSchema.parse(bucket)
  }
}

/** A bucketed cohort week — a validated `YYYY-Www` ISO-week string. */
export type CohortWeek = z.infer<typeof CoreAnalytics.CohortWeekSchema>

////////////////////////////////////////////////////////////////////////////////
// Acquisition — compound type
////////////////////////////////////////////////////////////////////////////////

/**
 * The shape of one captured acquisition attribution: the granular open
 * `source` (nullable) paired with the derived coarse `category` (always
 * present) and the specific `campaign` name (nullable). Composed from the
 * `CoreAnalytics` acquisition schemas so there is one source of truth for
 * the charset/length bound (shared by `source` and `campaign`) and the
 * closed category enum.
 *
 * Defined below `CoreAnalytics` because it references the class's static
 * `AcquisitionSourceSchema` / `AcquisitionCategorySchema` members.
 *
 * An `Acquisition` is always a *real* captured attribution — `category` is
 * never `null` (`campaign` | `organic` | `referral` | `direct`); `source`
 * and `campaign` are each independently nullable (`campaign` is non-null
 * only when a `utm_campaign` tag was present). "Never captured" is a null
 * `Acquisition` at the `learners` level, not a null `category` — see the
 * file header § "Three states".
 */
export const AcquisitionSchema = z.object({
  source: CoreAnalytics.AcquisitionSourceSchema.nullable(),
  category: CoreAnalytics.AcquisitionCategorySchema,
  campaign: CoreAnalytics.AcquisitionSourceSchema.nullable(),
})

/**
 * A captured acquisition attribution: the plain compound
 * `{ source: string | null; category: AcquisitionCategory; campaign: string
 * | null }` that {@link CoreAnalytics.classifyAcquisition} returns and that
 * the wire embeds (`LearnerSchema.acquisition`, the /me response, the auth
 * flow rows) as a plain `{ source, category, campaign } | null`. Flattened
 * to scalars at the structural edges (query params, Umami event / session
 * props); see the file header § "DB shape".
 */
export type Acquisition = z.infer<typeof AcquisitionSchema>
